// Admin → Rooms (v1.30) — manage the "See it in your space" scene catalogue.
// Two surfaces per scene:
//   1. Regenerate image: edit the prompt → Gemini Nano Banana re-renders the
//      photo and the cache-busted version bumps automatically.
//   2. Slot inputs: tune the artwork's wall position without re-rendering.

import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { invalidateRoomScenes } from "../../lib/roomScenes";
import { Loader2, Sparkles, Save, Image as ImageIcon } from "lucide-react";

function SlotPreview({ scene }) {
  // Tiny live preview that mirrors the slot rectangle on top of the room
  // photo at 100% width — so admins see immediately where the artwork sits
  // when they tweak top / left / width / height.
  return (
    <div
      className="relative border border-[#E5E0D8] overflow-hidden"
      style={{ aspectRatio: String(scene.image_aspect || 4 / 3) }}
    >
      <img
        src={scene.image_url}
        alt={scene.label}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div
        className="absolute border-2 border-[#F4C430] bg-[#1E3A8A]/30"
        style={{
          top: `${scene.slot.top}%`,
          left: `${scene.slot.left}%`,
          width: `${scene.slot.width}%`,
          height: `${scene.slot.height}%`,
        }}
      />
    </div>
  );
}

function SceneCard({ scene, onUpdated }) {
  const [prompt, setPrompt] = useState(scene.prompt || "");
  const [slot, setSlot] = useState(scene.slot);
  const [regenerating, setRegenerating] = useState(false);
  const [savingSlot, setSavingSlot] = useState(false);

  // Live-update local slot when the scene prop changes (e.g. after a save).
  useEffect(() => {
    setSlot(scene.slot);
    setPrompt(scene.prompt || "");
  }, [scene.image_version, scene.slot, scene.prompt]);

  const regenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Prompt cannot be empty");
      return;
    }
    setRegenerating(true);
    try {
      const { data } = await api.post(`/admin/rooms/${scene.scene_id}/regenerate`, { prompt });
      toast.success(`${scene.label} re-rendered`);
      invalidateRoomScenes();
      onUpdated && onUpdated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Regenerate failed");
    } finally {
      setRegenerating(false);
    }
  };

  const saveSlot = async () => {
    setSavingSlot(true);
    try {
      const { data } = await api.put(`/admin/rooms/${scene.scene_id}`, { slot });
      toast.success(`${scene.label} slot saved`);
      invalidateRoomScenes();
      onUpdated && onUpdated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingSlot(false);
    }
  };

  return (
    <div data-testid={`scene-card-${scene.scene_id}`} className="border border-[#E5E0D8] bg-white p-5">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-serif-display text-xl text-[#111]">{scene.label}</h3>
        <p className="font-mono-aam text-[10px] text-[#888]">{scene.scene_id}</p>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Live preview with slot overlay */}
        <SlotPreview scene={{ ...scene, slot }} />

        {/* Slot inputs */}
        <div>
          <p className="label-eyebrow text-[#1E3A8A]">— Slot position (%)</p>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {["top", "left", "width", "height"].map((key) => (
              <label key={key} className="flex flex-col text-xs text-[#555]">
                <span className="uppercase tracking-wider mb-1">{key}</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  data-testid={`scene-slot-${scene.scene_id}-${key}`}
                  value={slot[key] ?? 0}
                  onChange={(e) => setSlot((s) => ({ ...s, [key]: Number(e.target.value) }))}
                  className="border border-[#E5E0D8] px-2 py-1.5 font-mono-aam text-sm focus:outline-none focus:border-[#1E3A8A]"
                />
              </label>
            ))}
          </div>
          <button
            type="button"
            onClick={saveSlot}
            disabled={savingSlot}
            data-testid={`save-slot-${scene.scene_id}`}
            className="mt-3 inline-flex items-center gap-2 border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white px-3 py-1.5 text-xs uppercase tracking-wider disabled:opacity-50"
          >
            <Save className="w-3.5 h-3.5" /> {savingSlot ? "Saving…" : "Save slot"}
          </button>
        </div>
      </div>

      {/* Prompt + regenerate */}
      <div className="mt-6">
        <p className="label-eyebrow text-[#1E3A8A]">— Prompt</p>
        <textarea
          data-testid={`scene-prompt-${scene.scene_id}`}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={4}
          className="mt-2 w-full border border-[#E5E0D8] bg-white p-3 text-sm focus:outline-none focus:border-[#1E3A8A]"
          placeholder="Describe the room — furniture, palette, lighting, mood…"
        />
        <p className="text-[10px] text-[#888] mt-1 font-mono-aam">
          The shared empty-wall instructions are appended automatically; just describe the room itself.
        </p>
        <button
          type="button"
          onClick={regenerate}
          disabled={regenerating}
          data-testid={`regenerate-${scene.scene_id}`}
          className="mt-3 inline-flex items-center gap-2 bg-[#111] hover:bg-[#1E3A8A] text-white px-4 py-2 text-xs uppercase tracking-wider disabled:opacity-50"
        >
          {regenerating ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…
            </>
          ) : (
            <>
              <Sparkles className="w-3.5 h-3.5" /> Regenerate image
            </>
          )}
        </button>
        {scene.last_rendered_at && (
          <p className="text-[10px] text-[#888] mt-2 font-mono-aam">
            Last rendered: {new Date(scene.last_rendered_at).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}

export default function AdminRooms() {
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/rooms");
      setScenes(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load rooms");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onUpdated = (updated) => {
    setScenes((list) => list.map((s) => (s.scene_id === updated.scene_id ? updated : s)));
  };

  return (
    <div data-testid="admin-rooms" className="p-8 lg:p-12">
      <p className="label-eyebrow text-[#1E3A8A] flex items-center gap-2">
        <ImageIcon className="w-3.5 h-3.5" />— Mockup catalogue
      </p>
      <h1 className="font-serif-display text-4xl font-medium tracking-tight text-[#111] mt-2">
        Room scenes
      </h1>
      <p className="text-sm text-[#555] mt-2 max-w-2xl">
        The six interiors used in the artwork detail page&apos;s &quot;See it in your space&quot; preview.
        Re-render any scene with a new prompt via Gemini Nano Banana, or tune the wall slot
        coordinates without regenerating. Changes are visible to every visitor on next page load.
      </p>

      {loading ? (
        <p className="label-eyebrow text-[#555] mt-10">Loading…</p>
      ) : (
        <div className="mt-10 grid grid-cols-1 xl:grid-cols-2 gap-6">
          {scenes.map((s) => (
            <SceneCard key={s.scene_id} scene={s} onUpdated={onUpdated} />
          ))}
        </div>
      )}
    </div>
  );
}
